import { useEffect, useState } from 'react';
import { MdWarning, MdError, MdInfo, MdCheckCircle } from 'react-icons/md';

export default function ConfirmDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Potvrdi', 
  cancelText = 'Otkaži',
  type = 'warning' // warning, danger, info
}) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isOpen) {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) setBusy(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const iconColors = {
    warning: 'text-yellow-500',
    danger: 'text-red-500',
    info: 'text-blue-500',
  };

  const iconComponents = {
    warning: <MdWarning className="text-4xl" />,
    danger: <MdError className="text-4xl" />,
    info: <MdInfo className="text-4xl" />,
  };

  const buttonColors = {
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    danger: 'bg-red-600 hover:bg-red-700',
    info: 'bg-blue-600 hover:bg-blue-700',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-4">
          <div className={`flex-shrink-0 ${iconColors[type]}`}>
            {iconComponents[type]}
          </div>
          <div className="flex-1">
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {title}
              </h3>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {message}
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              if (busy || typeof onConfirm !== 'function') return;
              setBusy(true);
              try {
                const ret = onConfirm();
                if (ret != null && typeof ret.then === 'function') await ret;
                onClose();
              } catch {
                /* ostavi dijalog otvoren; pozivalac može da prikaže grešku */
              } finally {
                setBusy(false);
              }
            }}
            className={`px-4 py-2 rounded-xl text-white transition-colors font-medium disabled:opacity-60 ${buttonColors[type]}`}
          >
            {busy ? '…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

